import { useEffect, useRef, useState } from "react";

export interface SectionItem {
  id: string;
  label: string;
  hint?: string;
  disabled?: boolean;
}

interface Props {
  title: string;
  helpText: string;
  selected: SectionItem[];
  available: SectionItem[];
  emptyAvailableHint?: string;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
}

/**
 * Collapsible card with a "+" picker for skills/connectors. Matches the
 * Figma agent-builder layout: header with title, "+" trigger and chevron;
 * body shows selected items as removable chips above a help line. The
 * picker is a same-card popover listing the still-unselected available
 * options. No portal, no overlay system — keeps the section self-contained.
 */
export function AgentSection({
  title,
  helpText,
  selected,
  available,
  emptyAvailableHint,
  onAdd,
  onRemove,
  disabled = false,
}: Props) {
  const [expanded, setExpanded] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const pickerRef = useRef<HTMLDivElement | null>(null);

  const selectedIds = new Set(selected.map((s) => s.id));
  const candidates = available.filter((a) => !selectedIds.has(a.id));
  const filtered = query.trim()
    ? candidates.filter((c) => c.label.toLowerCase().includes(query.trim().toLowerCase()))
    : candidates;

  useEffect(() => {
    if (!pickerOpen) return;
    function onDocClick(e: MouseEvent) {
      if (!pickerRef.current?.contains(e.target as Node)) setPickerOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setPickerOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [pickerOpen]);

  function openPicker() {
    if (disabled) return;
    setExpanded(true);
    setQuery("");
    setPickerOpen((o) => !o);
  }

  return (
    <section className="agent-section" aria-disabled={disabled}>
      <header className="agent-section-header">
        <div className="agent-section-title-row">
          <span className="agent-section-title">{title}</span>
          <button
            type="button"
            className="agent-section-add"
            onClick={openPicker}
            disabled={disabled}
            aria-label={`Добавить: ${title}`}
            aria-expanded={pickerOpen}
          >
            <PlusIcon />
          </button>
        </div>
        <button
          type="button"
          className="agent-section-toggle"
          onClick={() => setExpanded((e) => !e)}
          aria-label={expanded ? "Свернуть" : "Развернуть"}
        >
          <ChevronIcon up={expanded} />
        </button>
      </header>

      {expanded && (
        <div className="agent-section-body">
          {selected.length > 0 && (
            <ul className="agent-section-chips">
              {selected.map((s) => (
                <li key={s.id} className="agent-section-chip" title={s.hint}>
                  <span className="agent-section-chip-label">{s.label}</span>
                  <button
                    type="button"
                    className="agent-section-chip-remove"
                    onClick={() => onRemove(s.id)}
                    disabled={disabled}
                    aria-label={`Убрать: ${s.label}`}
                  >
                    <CloseIcon />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="agent-section-help">{helpText}</p>
        </div>
      )}

      {pickerOpen && (
        <div className="agent-section-picker" ref={pickerRef} role="listbox">
          {available.length === 0 ? (
            <span className="agent-section-picker-empty">
              {emptyAvailableHint ?? "Нет доступных пунктов"}
            </span>
          ) : (
            <>
              <input
                type="search"
                className="agent-section-picker-search"
                placeholder="Поиск…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
              <div className="agent-section-picker-list">
                {filtered.length === 0 && (
                  <span className="agent-section-picker-empty">
                    {candidates.length === 0
                      ? "Все доступные уже добавлены"
                      : "Ничего не найдено"}
                  </span>
                )}
                {filtered.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="agent-section-picker-item"
                    onClick={() => {
                      onAdd(c.id);
                      setPickerOpen(false);
                    }}
                    disabled={c.disabled}
                    title={c.hint}
                  >
                    <span className="agent-section-picker-item-label">{c.label}</span>
                    {c.hint && <span className="agent-section-picker-item-hint">{c.hint}</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 2.5v9M2.5 7h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon({ up }: { up: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      style={{ transform: up ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
    >
      <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
